import { useQuery } from '@tanstack/react-query'
import { Tag } from 'antd'
import { reportFilterFields, type ReportDimension } from '@super-mega/server/contracts/analytics'
import { useTRPC } from '@/trpc/react.ts'
import { REPORT_LABELS } from './const/reports.ts'
import type { ReportSearch } from './report-search.ts'
import type { ReportNavigate } from './report-table.tsx'
import { reportQueryOptions } from './utils/report-query.ts'

function DimensionTags({
  dimension,
  ids,
  remove
}: {
  dimension: ReportDimension
  ids: number[]
  remove: (id: number) => void
}) {
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.analytics.options.queryOptions({ dimension, keyword: '', selectedIds: ids }),
    ...reportQueryOptions
  })
  const names = new Map(query.data?.map((item) => [item.id, item.name]))
  return ids.map((id) => {
    const name = names.get(id)
    const label = `${REPORT_LABELS[dimension]}：${name ? `${name} (#${id})` : `ID ${id}`}`
    return (
      <Tag
        key={id}
        className="m-0! inline-flex! max-w-full items-center gap-1 py-1!"
        closable={{ 'aria-label': `移除${label}` }}
        onClose={(event) => {
          event.preventDefault()
          remove(id)
        }}
      >
        <span className="max-w-72 truncate" title={label}>
          {label}
        </span>
      </Tag>
    )
  })
}

export function ReportAppliedFilters({ search, navigate }: { search: ReportSearch; navigate: ReportNavigate }) {
  const dimensions = (Object.keys(REPORT_LABELS) as ReportDimension[]).filter(
    (key) => search[reportFilterFields[key]]?.length
  )
  if (!dimensions.length) {
    return null
  }
  return (
    <div className="border-border mt-3 flex flex-wrap items-start gap-2 border-t pt-3" aria-label="已生效的组合筛选">
      <span className="text-content-muted py-1 text-xs">已生效</span>
      <div className="flex max-h-28 min-w-0 flex-1 flex-wrap gap-2 overflow-y-auto">
        {dimensions.map((dimension) => (
          <DimensionTags
            key={dimension}
            dimension={dimension}
            ids={search[reportFilterFields[dimension]]!}
            remove={(id) =>
              void navigate({
                search: (previous) => {
                  const field = reportFilterFields[dimension]
                  const ids = previous[field]?.filter((value) => value !== id)
                  return { ...previous, [field]: ids?.length ? ids : undefined, current: 1 }
                }
              })
            }
          />
        ))}
      </div>
    </div>
  )
}
